import React, {
  useCallback,
  useMemo,
  useState,
  useRef,
  useEffect,
} from "react";
import { Button } from "@/components/ui/button";
import { AutoExpandingTextarea } from "@/components/ui/auto-expanding-textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronDown } from "lucide-react";
import {
  MessageSquare,
  Loader2,
  X,
  Paperclip,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Minimize2,
  RotateCcw,
  Send,
  Info,
  Sparkles,
} from "lucide-react";
import { streamChat, ReasoningLevel } from "@/services/chatService";
import type { Interview } from "@/services/interviewService";
import { cn } from "@/lib/utils";
import { MarkdownMessage } from "@/components/ui/markdown-message";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { track } from "@vercel/analytics";

// Chat conversation types
interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  images?: Array<{ mime: string; b64: string }>;
}

interface StoredConversation {
  id: string;
  lastResponseId?: string; // Only store the latest OpenAI response ID
  recentMessages: ChatMessage[]; // Store last few exchanges for display
  reasoning: ReasoningLevel;
  createdAt: string;
  lastUpdated: string;
  messageCount: number;
}

interface ResizableChatSidebarProps {
  projectId: string;
  interviews: Interview[];
  isOpen: boolean;
  onToggle: (open: boolean) => void;
  width: number;
  onWidthChange: (width: number) => void;
}

const MAX_MESSAGES = 24; // Warn after 24 messages (12 exchanges)
const WARNING_INTERVAL = 6; // Show warning every 6 exchanges (12 messages) after MAX_MESSAGES
const INITIAL_RENDER_LIMIT = 12; // how many to render initially
const CHUNK_SIZE = 8; // how many older messages to load per scroll
const STORAGE_LIMIT = 100; // how many to keep in localStorage (increased for scroll loading)

export const ResizableChatSidebar: React.FC<ResizableChatSidebarProps> = ({
  projectId,
  interviews,
  isOpen,
  onToggle,
  width,
  onWidthChange,
}) => {
  const [msg, setMsg] = useState("");
  const [reasoning, setReasoning] = useState<ReasoningLevel>("low");
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentStreamingMessage, setCurrentStreamingMessage] = useState("");
  const [images, setImages] = useState<Array<{ mime: string; b64: string }>>(
    []
  );
  const [abortController, setAbortController] =
    useState<AbortController | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showResetWarning, setShowResetWarning] = useState(false);

  // Interview filtering state
  const [selectedInterviewIds, setSelectedInterviewIds] = useState<string[]>(
    []
  );
  const [showInterviewFilter, setShowInterviewFilter] = useState(false);
  const [filterSearch, setFilterSearch] = useState("");
  const [groupBy, setGroupBy] = useState<"none" | "angle" | "company">("angle");
  const [angleFilters, setAngleFilters] = useState<string[]>([]);

  // Conversation state
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [lastResponseId, setLastResponseId] = useState<string | null>(null);
  const [messageCount, setMessageCount] = useState(0);
  const [recentMessages, setRecentMessages] = useState<ChatMessage[]>([]);
  const [lastWarningCount, setLastWarningCount] = useState(0);

  // Scroll loading state
  const [renderedCount, setRenderedCount] = useState(INITIAL_RENDER_LIMIT);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [userIsAtBottom, setUserIsAtBottom] = useState(true);

  // Animation state for "first time" prompt
  const [hasSeenPrompt, setHasSeenPrompt] = useState(false);
  const [showCallout, setShowCallout] = useState(true);

  // Image popup state
  const [selectedImage, setSelectedImage] = useState<{
    mime: string;
    b64: string;
  } | null>(null);
  const [popupPosition, setPopupPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const resizeRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const visibleMessages = useMemo(() => {
    const totalAvailable = recentMessages.length;
    const toRender = Math.min(renderedCount, totalAvailable);
    return recentMessages.slice(-toRender);
  }, [recentMessages, renderedCount]);

  const readyInterviews = useMemo(
    () =>
      interviews
        .filter((i) => i.transcriptStatus === "ready")
        .sort((a, b) => {
          // Sort by date descending (most recent first)
          const dateA = a.date ? new Date(a.date).getTime() : 0;
          const dateB = b.date ? new Date(b.date).getTime() : 0;
          return dateB - dateA;
        }),
    [interviews]
  );

  // Get unique angles and companies for filtering
  const uniqueAngles = useMemo(() => {
    const angles = new Set<string>();
    readyInterviews.forEach((i) => {
      if (i.angle && i.angle.trim()) angles.add(i.angle.trim());
    });
    return Array.from(angles).sort();
  }, [readyInterviews]);

  // Filter interviews by search and angle
  const filteredInterviews = useMemo(() => {
    return readyInterviews.filter((interview) => {
      // Search filter
      if (filterSearch) {
        const search = filterSearch.toLowerCase();
        const matchesSearch =
          interview.expertFirstName?.toLowerCase().includes(search) ||
          interview.expertLastName?.toLowerCase().includes(search) ||
          interview.company?.toLowerCase().includes(search) ||
          interview.angle?.toLowerCase().includes(search) ||
          interview.interviewDescription?.toLowerCase().includes(search);
        if (!matchesSearch) return false;
      }
      // Angle filter (multiple selection)
      if (
        angleFilters.length > 0 &&
        !angleFilters.includes(interview.angle || "")
      )
        return false;
      return true;
    });
  }, [readyInterviews, filterSearch, angleFilters]);

  // Group interviews by angle or company
  const groupedInterviews = useMemo(() => {
    if (groupBy === "none") {
      return { "All Interviews": filteredInterviews };
    }

    const groups: Record<string, Interview[]> = {};
    filteredInterviews.forEach((interview) => {
      const key =
        groupBy === "angle"
          ? interview.angle?.trim() || "No Angle"
          : interview.company?.trim() || "No Company";
      if (!groups[key]) groups[key] = [];
      groups[key].push(interview);
    });

    // Sort groups alphabetically, but put "No Angle" / "No Company" at the end
    const sortedKeys = Object.keys(groups).sort((a, b) => {
      if (a.startsWith("No ")) return 1;
      if (b.startsWith("No ")) return -1;
      return a.localeCompare(b);
    });

    const sorted: Record<string, Interview[]> = {};
    sortedKeys.forEach((key) => {
      sorted[key] = groups[key];
    });
    return sorted;
  }, [filteredInterviews, groupBy]);

  // Toggle all interviews in a group
  const toggleGroupSelection = (groupInterviews: Interview[]) => {
    const groupIds = groupInterviews.map((i) => i.id);
    const allSelected = groupIds.every((id) =>
      selectedInterviewIds.includes(id)
    );

    if (allSelected) {
      // Deselect all in group
      setSelectedInterviewIds((prev) =>
        prev.filter((id) => !groupIds.includes(id))
      );
    } else {
      // Select all in group
      setSelectedInterviewIds((prev) => {
        const newIds = groupIds.filter((id) => !prev.includes(id));
        return [...prev, ...newIds];
      });
    }
  };

  // Handle image click to show popup
  const handleImageClick = useCallback(
    (
      image: { mime: string; b64: string },
      event: React.MouseEvent<HTMLImageElement>
    ) => {
      // Prevent the click from bubbling and triggering click outside
      event.stopPropagation();

      const rect = event.currentTarget.getBoundingClientRect();
      const x = rect.left + rect.width / 2; // Center horizontally on image
      const y = rect.top - 10; // Position above the image

      setSelectedImage(image);
      setPopupPosition({ x, y });
    },
    []
  );

  // Handle escape key or click outside to close popup
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && selectedImage) {
        setSelectedImage(null);
        setPopupPosition(null);
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (selectedImage) {
        setSelectedImage(null);
        setPopupPosition(null);
      }
    };

    if (selectedImage) {
      document.addEventListener("keydown", handleEscape);
      // Delay adding click outside handler to prevent immediate closure
      const timeoutId = setTimeout(() => {
        document.addEventListener("click", handleClickOutside);
      }, 50);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener("keydown", handleEscape);
        document.removeEventListener("click", handleClickOutside);
      };
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("click", handleClickOutside);
    };
  }, [selectedImage]);

  // Cleanup effect: abort any ongoing streaming requests when component unmounts
  useEffect(() => {
    return () => {
      if (abortController) {
        console.log("Component unmounting - aborting active chat request");
        abortController.abort();
      }
    };
  }, [abortController]);

  // Storage key for this project's conversation
  const storageKey = `chat_conversation_${projectId}`;

  // Load conversation from localStorage on mount or project change
  useEffect(() => {
    if (projectId) {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        try {
          const conversation: StoredConversation = JSON.parse(stored);
          setConversationId(conversation.id);
          setLastResponseId(conversation.lastResponseId || null);
          setMessageCount(conversation.messageCount || 0);
          setRecentMessages(
            (conversation.recentMessages || []).map((msg) => ({
              ...msg,
              timestamp: new Date(msg.timestamp),
            }))
          );
          setReasoning(conversation.reasoning);
        } catch (error) {
          console.error("Failed to load conversation:", error);
          // Clear invalid data
          localStorage.removeItem(storageKey);
          // Reset to empty state
          setConversationId(null);
          setLastResponseId(null);
          setMessageCount(0);
          setRecentMessages([]);
          setCurrentStreamingMessage("");
          setRenderedCount(INITIAL_RENDER_LIMIT);
          setUserIsAtBottom(true);
        }
      } else {
        // No stored conversation for this project - reset to empty state
        setConversationId(null);
        setLastResponseId(null);
        setMessageCount(0);
        setRecentMessages([]);
        setCurrentStreamingMessage("");
        setRenderedCount(INITIAL_RENDER_LIMIT);
        setUserIsAtBottom(true);
      }
    }
  }, [projectId, storageKey]);

  // Save conversation to localStorage
  const saveConversation = useCallback(
    (convId: string, responseId?: string, messages?: ChatMessage[]) => {
      const conversation: StoredConversation = {
        id: convId,
        lastResponseId: responseId,
        recentMessages: messages || recentMessages,
        reasoning,
        createdAt: conversationId
          ? JSON.parse(localStorage.getItem(storageKey) || "{}").createdAt ||
            new Date().toISOString()
          : new Date().toISOString(),
        lastUpdated: new Date().toISOString(),
        messageCount,
      };
      localStorage.setItem(storageKey, JSON.stringify(conversation));
    },
    [reasoning, storageKey, conversationId, messageCount, recentMessages]
  );

  // Create new conversation ID
  const createConversationId = () => {
    return `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  // Check if user is near bottom of scroll container
  const checkIfUserAtBottom = useCallback((container: HTMLElement) => {
    const { scrollTop, scrollHeight, clientHeight } = container;
    const threshold = 50; // pixels from bottom - more lenient for detecting return to bottom
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - threshold;
    setUserIsAtBottom(isAtBottom);
    return isAtBottom;
  }, []);

  // Load older messages when scrolling to top
  const loadOlderMessages = useCallback(() => {
    if (isLoadingOlder || renderedCount >= recentMessages.length) return;

    const container = messagesContainerRef.current;
    if (!container) return;

    setIsLoadingOlder(true);

    // Store current scroll state before loading
    const beforeScrollHeight = container.scrollHeight;
    const beforeScrollTop = container.scrollTop;

    // Simulate loading delay for better UX
    setTimeout(() => {
      setRenderedCount((prev) =>
        Math.min(prev + CHUNK_SIZE, recentMessages.length)
      );

      // Restore scroll position after DOM updates
      requestAnimationFrame(() => {
        const afterScrollHeight = container.scrollHeight;
        const heightDiff = afterScrollHeight - beforeScrollHeight;
        container.scrollTop = beforeScrollTop + heightDiff;
        setIsLoadingOlder(false);
      });
    }, 200);
  }, [isLoadingOlder, renderedCount, recentMessages.length]);

  // Handle scroll detection for loading older messages
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const container = e.currentTarget;
      const scrollTop = container.scrollTop;
      const scrollThreshold = 50; // pixels from top to trigger load

      // Check if user is at bottom (for smart auto-scroll)
      checkIfUserAtBottom(container);

      if (
        scrollTop <= scrollThreshold &&
        !isLoadingOlder &&
        renderedCount < recentMessages.length
      ) {
        loadOlderMessages();
      }
    },
    [
      isLoadingOlder,
      renderedCount,
      recentMessages.length,
      loadOlderMessages,
      checkIfUserAtBottom,
    ]
  );

  // Prevent scroll propagation to dashboard when interacting with chat sidebar
  const handleWheel = useCallback((e: React.WheelEvent) => {
    // Always prevent propagation for trackpad scrolling
    e.stopPropagation();

    const container = messagesContainerRef.current;
    if (!container) {
      e.preventDefault();
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isScrollable = scrollHeight > clientHeight;

    if (!isScrollable) {
      // If content isn't scrollable, prevent default behavior
      e.preventDefault();
      return;
    }

    // Detect upward scrolling (negative deltaY) and disable auto-scroll
    // This gives user immediate control when they try to scroll up
    if (e.deltaY < 0) {
      setUserIsAtBottom(false);
    }

    // Allow normal scrolling within the container, but prevent propagation
    // The container will handle its own scrolling
  }, []);

  // Handle wheel events on the entire sidebar to prevent dashboard scrolling
  const handleSidebarWheel = useCallback((e: React.WheelEvent) => {
    // Only prevent propagation, not default behavior (allow internal scrolling)
    e.stopPropagation();
  }, []);

  // Additional handlers for touch/trackpad events
  const handleSidebarTouchMove = useCallback((e: React.TouchEvent) => {
    e.stopPropagation();
  }, []);

  // Handle all scroll-related events on the sidebar
  const handleSidebarScroll = useCallback((e: React.UIEvent) => {
    e.stopPropagation();
  }, []);

  // Prevent pointer events from bubbling to dashboard
  const handleSidebarPointerMove = useCallback((e: React.PointerEvent) => {
    e.stopPropagation();
  }, []);

  // Reset conversation
  const resetConversation = useCallback(() => {
    const newConvId = createConversationId();
    setConversationId(newConvId);
    setLastResponseId(null);
    setMessageCount(0);
    setRecentMessages([]);
    setCurrentStreamingMessage("");
    setMsg("");
    setImages([]);
    setShowResetWarning(false);
    setLastWarningCount(0);
    setRenderedCount(INITIAL_RENDER_LIMIT);
    setUserIsAtBottom(true);
    localStorage.removeItem(storageKey);
  }, [storageKey]);

  // Auto-scroll to bottom only for new messages (not when loading older ones)
  const lastMessageCountRef = useRef(0);
  useEffect(() => {
    // Only auto-scroll if:
    // 1. Not loading older messages
    // 2. User is near the bottom (hasn't scrolled up intentionally)
    // 3. Message count increased (new message added) OR streaming content changed
    const messageCountIncreased =
      recentMessages.length > lastMessageCountRef.current;

    if (
      !isLoadingOlder &&
      userIsAtBottom &&
      (messageCountIncreased || currentStreamingMessage)
    ) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }

    lastMessageCountRef.current = recentMessages.length;
  }, [recentMessages, currentStreamingMessage, isLoadingOlder, userIsAtBottom]);

  // Reset rendered count when new messages are added (keep showing recent messages)
  // Only when not actively loading older messages
  useEffect(() => {
    const totalMessages = recentMessages.length;
    if (
      totalMessages > 0 &&
      renderedCount < INITIAL_RENDER_LIMIT &&
      !isLoadingOlder
    ) {
      setRenderedCount(Math.min(INITIAL_RENDER_LIMIT, totalMessages));
    }
  }, [recentMessages.length, renderedCount, isLoadingOlder]);

  // Check if we should show reset warning
  useEffect(() => {
    if (messageCount >= MAX_MESSAGES) {
      // Show warning at 24, then every 12 messages after that (36, 48, 60...)
      if (
        messageCount === MAX_MESSAGES ||
        messageCount - lastWarningCount >= WARNING_INTERVAL
      ) {
        setShowResetWarning(true);
        setLastWarningCount(messageCount);
      }
    }
  }, [messageCount, lastWarningCount]);

  // Handle resize functionality
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      const startX = e.clientX;
      const startWidth = width;

      const handleMouseMove = (e: MouseEvent) => {
        const deltaX = startX - e.clientX; // Reverse direction since we're resizing from the left
        const newWidth = Math.max(280, Math.min(1000, startWidth + deltaX));
        onWidthChange(newWidth);
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [width, onWidthChange]
  );

  const onPaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        if (it && it.type && it.type.startsWith("image/")) {
          const file = it.getAsFile();
          if (file) {
            const reader = new FileReader();
            reader.onload = () => {
              const result = reader.result as string;
              const mime = file.type || "image/png";
              const b64 = result.split(",")[1];
              setImages((prev) => [...prev, { mime, b64 }]);
            };
            reader.readAsDataURL(file);
          }
        }
      }
    },
    []
  );

  const removeImage = (idx: number) => {
    setImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const toggleInterviewSelection = (interviewId: string) => {
    setSelectedInterviewIds((prev) =>
      prev.includes(interviewId)
        ? prev.filter((id) => id !== interviewId)
        : [...prev, interviewId]
    );
  };

  const cancelChat = () => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
      setIsStreaming(false);
      setCurrentStreamingMessage("");
    }
  };

  const send = async () => {
    if (!msg.trim() || isStreaming) return;

    // Create conversation ID if this is the first message
    let convId = conversationId;
    if (!convId) {
      convId = createConversationId();
      setConversationId(convId);
    }

    // Create user message
    const userMessage: ChatMessage = {
      id: `msg_${Date.now()}_user`,
      role: "user",
      content: msg.trim(),
      timestamp: new Date(),
      images: images.length > 0 ? images : undefined,
    };

    // Optimistically show the user message and persist
    const newRecentAfterUser = [...recentMessages, userMessage].slice(
      -STORAGE_LIMIT
    );
    setRecentMessages(newRecentAfterUser);
    setMessageCount((prev) => prev + 1);
    saveConversation(convId, lastResponseId || undefined, newRecentAfterUser);

    // Track chat message sent
    track("chat_message_sent", {
      project_id: projectId || "unknown",
      has_images: images.length > 0,
      message_length: msg.trim().length,
      is_new_conversation: !conversationId,
      reasoning_level: reasoning,
    });

    // Clear input and start streaming
    setCurrentStreamingMessage("");
    setMsg("");
    setImages([]);
    setIsStreaming(true);
    setUserIsAtBottom(true); // Reset to bottom when user sends a message

    // Create abort controller for cancellation
    const controller = new AbortController();
    setAbortController(controller);

    let assistantResponse = "";
    let assistantResponseId: string | undefined;
    let streamErrored = false;

    let shouldRetryWithoutResponseId = false;

    const attemptStreamChat = async (
      useResponseId: boolean,
      includeHistory: boolean = false
    ) => {
      // Prepare conversation history for context recovery
      let conversationHistory: Array<{ role: string; content: string }> = [];
      if (includeHistory && recentMessages.length > 0) {
        // Include recent messages for context (limit to last 10 messages to avoid token limits)
        conversationHistory = recentMessages.slice(-10).map((msg) => ({
          role: msg.role,
          content: msg.content,
        }));
      }

      return streamChat(
        {
          projectId,
          message: userMessage.content,
          reasoning,
          images: userMessage.images,
          conversationId: convId,
          previousResponseId: useResponseId ? lastResponseId : undefined,
          conversationHistory:
            conversationHistory.length > 0 ? conversationHistory : undefined,
          interviewIds:
            selectedInterviewIds.length > 0 ? selectedInterviewIds : undefined,
        },
        (delta) => {
          assistantResponse += delta;
          setCurrentStreamingMessage(assistantResponse);
        },
        (err) => {
          console.error("stream error:", err);
          // Check if this is an invalid response ID error
          if (
            err.includes("previous_response_not_found") ||
            err.includes("Previous response") ||
            err.includes("not found")
          ) {
            shouldRetryWithoutResponseId = true;
            console.log(
              "Invalid response ID detected, will retry with conversation history"
            );
          } else {
            streamErrored = true;
          }
        },
        (responseId) => {
          assistantResponseId = responseId;
          console.log("Received response ID:", responseId);
        },
        controller.signal
      );
    };

    try {
      // First attempt with response ID (if available)
      await attemptStreamChat(!!lastResponseId);

      // If we got an invalid response ID error, retry with conversation history
      if (shouldRetryWithoutResponseId && !controller.signal.aborted) {
        console.log(
          "Retrying chat with conversation history for context recovery"
        );

        // Clear the invalid response ID
        setLastResponseId(null);

        // Reset streaming state for retry
        assistantResponse = "";
        setCurrentStreamingMessage("");

        // Retry with conversation history instead of response ID
        await attemptStreamChat(false, true);

        // Add a subtle indicator that context was recovered
        if (assistantResponse.trim()) {
          assistantResponse =
            "*(Conversation context recovered from chat history)*\n\n" +
            assistantResponse;
        }
      }

      // Prepare assistant message content (fallback if empty)
      const finalContent =
        assistantResponse.trim().length > 0
          ? assistantResponse
          : streamErrored
          ? "Sorry—no response received (stream error)."
          : "Sorry—no response received.";

      // Create assistant message
      const assistantMessage: ChatMessage = {
        id: `msg_${Date.now()}_assistant`,
        role: "assistant",
        content: finalContent,
        timestamp: new Date(),
      };

      // Update recent messages (keep last STORAGE_LIMIT)
      const newRecentMessages = [...newRecentAfterUser, assistantMessage].slice(
        -STORAGE_LIMIT
      );
      setRecentMessages(newRecentMessages);

      // Update conversation state
      setLastResponseId(assistantResponseId || null);
      setMessageCount((prev) => prev + 1); // assistant bubble
      setCurrentStreamingMessage("");

      // Save conversation to localStorage
      saveConversation(convId, assistantResponseId, newRecentMessages);
    } catch (error: any) {
      // Handle abort specifically
      if (error.name === "AbortError" || controller.signal.aborted) {
        console.log("Chat request was cancelled");
        setCurrentStreamingMessage("");
        // Don't add error message for user-initiated cancellation
        return;
      }

      console.error("Chat error:", error);
      setCurrentStreamingMessage("");

      // Add an assistant error bubble for visibility
      const assistantMessage: ChatMessage = {
        id: `msg_${Date.now()}_assistant`,
        role: "assistant",
        content: "An error occurred while generating a response.",
        timestamp: new Date(),
      };
      const newRecentMessages = [...newRecentAfterUser, assistantMessage].slice(
        -STORAGE_LIMIT
      );
      setRecentMessages(newRecentMessages);
      setMessageCount((prev) => prev + 1);
      saveConversation(convId!, lastResponseId || undefined, newRecentMessages);
    } finally {
      setIsStreaming(false);
      setAbortController(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      // Only send if not streaming and there's a message
      if (!isStreaming && msg.trim()) {
        send();
      }
    }
  };

  const toggleMaximize = () => {
    if (isMaximized) {
      onWidthChange(400); // Reset to default width
      setIsMaximized(false);
    } else {
      onWidthChange(800); // Maximize
      setIsMaximized(true);
    }
  };

  // Check if we should show the animated button with tooltip
  const hasReadyTranscripts = interviews.some(
    (interview) => interview.transcriptStatus === "ready"
  );
  const hasNoConversationHistory = recentMessages.length === 0;
  const shouldShowAnimation =
    hasReadyTranscripts &&
    hasNoConversationHistory &&
    !hasSeenPrompt &&
    showCallout;

  // Auto-hide the callout after 5 seconds
  useEffect(() => {
    if (shouldShowAnimation) {
      const timer = setTimeout(() => {
        setShowCallout(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [shouldShowAnimation]);

  if (!isOpen) {
    return (
      <div className="fixed right-4 bottom-6 z-50">
        <div className="relative">
          <Button
            onClick={() => {
              setHasSeenPrompt(true);
              setShowCallout(false);
              onToggle(true);
            }}
            className={cn(
              "transition-all duration-200 gap-2 rounded-lg relative overflow-hidden",
              "bg-white dark:bg-[#2d2d2d] text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-[#404040] shadow-lg",
              "hover:shadow-xl hover:bg-gray-50 dark:hover:bg-[#333333]",
              shouldShowAnimation && [
                "scale-110", // Enlarge the button
                "bg-green-50 dark:bg-green-950 border-green-500 dark:border-green-600",
                "shadow-green-500/50 dark:shadow-green-600/50 shadow-xl",
                "before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white/30 before:to-transparent",
                "animate-shine", // Trigger the shine animation
              ]
            )}
            size="sm"
          >
            <MessageSquare
              className={cn(
                "h-4 w-4",
                shouldShowAnimation &&
                  "animate-[pulse_0.6s_ease-in-out_2] text-green-600 dark:text-green-400"
              )}
            />
            Chat
          </Button>

          {/* Callout pointing to the button */}
          {shouldShowAnimation && (
            <div className="absolute right-full mr-4 top-1/2 -translate-y-1/2 whitespace-nowrap animate-in slide-in-from-right-5 fade-in-0 duration-500">
              <div className="bg-gray-800 dark:bg-gray-100 text-white dark:text-gray-900 px-4 py-2 rounded-lg shadow-lg relative">
                <div className="text-sm font-medium">
                  💡 Click here to ask AI about your transcripts
                </div>
                {/* Arrow pointing right */}
                <div className="absolute left-full top-1/2 -translate-y-1/2">
                  <div className="border-8 border-transparent border-l-gray-800 dark:border-l-gray-100"></div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Overlay for when resizing */}
      {isResizing && (
        <div className="fixed inset-0 z-[120] cursor-col-resize" />
      )}

      <div
        ref={sidebarRef}
        className={cn(
          "resizable-chat-sidebar fixed top-0 right-0 h-full bg-white dark:bg-[#1e1e1e] border-l border-gray-200 dark:border-[#2d2d2d] shadow-xl z-[100] flex flex-col",
          "transition-all duration-300 ease-out"
        )}
        style={{
          width: `${width}px`,
          marginRight: "0px", // Ensure sidebar is at the very right edge
        }}
        onWheel={handleSidebarWheel}
        onTouchMove={handleSidebarTouchMove}
        onPointerMove={handleSidebarPointerMove}
        onScroll={handleSidebarScroll}
      >
        {/* Resize handle */}
        <div
          ref={resizeRef}
          className={cn(
            "absolute left-0 top-0 bottom-0 w-2 cursor-col-resize bg-transparent hover:bg-blue-500/10 transition-colors group",
            isResizing && "bg-blue-500/20"
          )}
          onMouseDown={handleMouseDown}
        >
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-12 bg-gray-300 dark:bg-gray-500 rounded-r opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between h-12 px-3 border-b border-gray-200 dark:border-[#2d2d2d] bg-gray-50 dark:bg-[#252526]">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-green-600 dark:text-green-400" />
            <div className="flex items-center gap-2">
              <h2 className="font-semibold text-sm text-gray-900 dark:text-gray-100">
                Project Chat
              </h2>
              {recentMessages.length > 0 && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  ({Math.ceil(recentMessages.length / 2)} exchanges)
                </span>
              )}
              {/* Info icon with hover card */}
              <HoverCard>
                <HoverCardTrigger asChild>
                  <div className="cursor-pointer p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                    <Info className="h-3 w-3 text-gray-500 dark:text-gray-400" />
                  </div>
                </HoverCardTrigger>
                <HoverCardContent
                  className="w-64 p-2 bg-white dark:bg-[#2d2d2d] border-gray-200 dark:border-[#404040]"
                  align="end"
                  sideOffset={2}
                >
                  <h4 className="text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">
                    Available Transcripts ({readyInterviews.length})
                  </h4>
                  {readyInterviews.length > 0 ? (
                    <div className="max-h-48 overflow-auto">
                      {readyInterviews.map((iv) => (
                        <div
                          key={iv.id}
                          className="text-xs text-gray-600 dark:text-gray-400 py-0.5"
                        >
                          <div className="font-medium truncate">
                            • {iv.expertFirstName} {iv.expertLastName}
                          </div>
                          {iv.interviewDescription && (
                            <div className="text-gray-500 dark:text-gray-500 ml-2 text-[10px] truncate leading-tight">
                              {iv.interviewDescription}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 dark:text-gray-500">
                      No transcripts available yet. Upload some interviews to
                      start chatting!
                    </p>
                  )}
                </HoverCardContent>
              </HoverCard>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {/* Reset conversation button */}
            {recentMessages.length > 0 && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                onClick={() => setShowResetWarning(true)}
                title="Reset conversation"
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              onClick={toggleMaximize}
            >
              {isMaximized ? (
                <Minimize2 className="h-4 w-4" />
              ) : (
                <Maximize2 className="h-4 w-4" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              onClick={() => onToggle(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Model Selection Bar */}
          <div className="p-3 border-b border-gray-200 dark:border-[#2d2d2d] bg-gray-50/50 dark:bg-[#252526]/50">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                Model
              </span>
              <div className="relative">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs bg-white dark:bg-[#2d2d2d] border border-gray-200 dark:border-[#404040] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#333333] hover:text-gray-900 dark:hover:text-gray-100"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                >
                  {reasoning === "low" ? "Balanced" : "Premium"}
                  <ChevronDown
                    className={`h-3 w-3 ml-1 transition-transform ${
                      dropdownOpen ? "rotate-180" : ""
                    }`}
                  />
                </Button>
                {dropdownOpen && (
                  <div className="absolute top-full right-0 mt-1 bg-white dark:bg-[#2d2d2d] border border-gray-200 dark:border-[#404040] rounded-lg shadow-lg z-10 overflow-hidden min-w-[140px]">
                    <button
                      className="w-full px-3 py-2 text-left hover:bg-green-50 dark:hover:bg-green-900/20 text-xs text-gray-700 dark:text-gray-300 hover:text-green-700 dark:hover:text-green-300 transition-colors"
                      onClick={() => {
                        setReasoning("low");
                        setDropdownOpen(false);
                      }}
                    >
                      <div className="font-medium">Balanced</div>
                      <div className="text-gray-500 dark:text-gray-400 text-xs">
                        GPT-5 Mini • Quick responses
                      </div>
                    </button>
                    <div className="h-px bg-gray-200 dark:bg-[#404040]" />
                    <button
                      className="w-full px-3 py-2 text-left hover:bg-green-50 dark:hover:bg-green-900/20 text-xs text-gray-700 dark:text-gray-300 hover:text-green-700 dark:hover:text-green-300 transition-colors"
                      onClick={() => {
                        setReasoning("medium");
                        setDropdownOpen(false);
                      }}
                    >
                      <div className="font-medium">Premium</div>
                      <div className="text-gray-500 dark:text-gray-400 text-xs">
                        GPT-5.2 • Best quality
                      </div>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Chat Messages Area */}
          <div className="flex-1 flex flex-col min-h-0">
            {/* Long conversation banner */}
            {messageCount >= MAX_MESSAGES && !showResetWarning && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 px-3 py-2 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Info className="h-4 w-4 text-amber-600 dark:text-amber-500 flex-shrink-0" />
                  <p className="text-xs text-amber-800 dark:text-amber-300 truncate">
                    Long conversation ({Math.floor(messageCount / 2)} exchanges)
                    - consider resetting for best performance
                  </p>
                </div>
                <Button
                  onClick={resetConversation}
                  size="sm"
                  className="h-6 px-2 text-xs bg-amber-600 hover:bg-amber-700 dark:bg-amber-600 dark:hover:bg-amber-700 text-white border-0 flex-shrink-0"
                >
                  Reset
                </Button>
              </div>
            )}
            {recentMessages.length === 0 ? (
              // Empty state
              <div className="flex-1 flex flex-col items-center justify-center text-center p-6">
                <div className="text-gray-400 dark:text-gray-500 mb-4">
                  <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm mb-2">
                    Start a conversation about your transcripts
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Ask questions, analyze insights, or explore your interview
                    data
                  </p>
                </div>

                {/* Available Transcripts in empty state */}
                {readyInterviews.length > 0 ? (
                  <div className="w-full max-w-sm p-3 bg-gray-50 dark:bg-[#252526] rounded-lg border border-gray-200 dark:border-[#2d2d2d]">
                    <h4 className="text-xs font-medium mb-2 text-gray-700 dark:text-gray-300">
                      Available Transcripts ({readyInterviews.length})
                    </h4>
                    <div className="space-y-1 max-h-20 overflow-auto">
                      {readyInterviews.slice(0, 3).map((iv) => (
                        <div
                          key={iv.id}
                          className="text-xs text-gray-600 dark:text-gray-400"
                        >
                          • {iv.expertFirstName} {iv.expertLastName}
                        </div>
                      ))}
                      {readyInterviews.length > 3 && (
                        <div className="text-xs text-gray-500 dark:text-gray-500">
                          +{readyInterviews.length - 3} more...
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="w-full max-w-sm p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                    <div className="flex items-start gap-2">
                      <Info className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                      <div className="text-left">
                        <h4 className="text-xs font-medium mb-1 text-amber-800 dark:text-amber-300">
                          No transcripts available
                        </h4>
                        <p className="text-xs text-amber-700 dark:text-amber-400">
                          Upload interview transcripts to start chatting about
                          them
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              // Message history
              <div
                ref={messagesContainerRef}
                className="flex-1 overflow-auto p-3 space-y-3"
                onScroll={handleScroll}
                onWheel={handleWheel}
              >
                {/* Loading indicator for older messages */}
                {isLoadingOlder && (
                  <div className="flex justify-center py-2">
                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Loading older messages...
                    </div>
                  </div>
                )}

                {/* Indicator if there are more messages available */}
                {!isLoadingOlder && renderedCount < recentMessages.length && (
                  <div className="flex justify-center py-2">
                    <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full">
                      {recentMessages.length - renderedCount} older messages
                      available - scroll up to load
                    </div>
                  </div>
                )}

                {visibleMessages.map((message, index) => (
                  <div
                    key={message.id || index}
                    className={`flex ${
                      message.role === "user" ? "justify-end" : "justify-start"
                    }`}
                  >
                    <div
                      className={`max-w-[85%] p-3 rounded-lg ${
                        message.role === "user"
                          ? "bg-green-600 text-white rounded-br-sm"
                          : "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-bl-sm"
                      }`}
                    >
                      {message.role === "assistant" ? (
                        <MarkdownMessage
                          content={message.content}
                          className="text-sm text-gray-900 dark:text-gray-100"
                        />
                      ) : (
                        <div className="text-sm whitespace-pre-wrap">
                          {message.content}
                        </div>
                      )}
                      {message.images && message.images.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {message.images.map((img, idx) => (
                            <img
                              key={idx}
                              alt={`attachment-${idx}`}
                              src={`data:${img.mime};base64,${img.b64}`}
                              className="h-16 w-16 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={(e) => handleImageClick(img, e)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {/* Current streaming response */}
                {isStreaming && (
                  <div className="flex justify-start">
                    <div className="max-w-[85%] p-3 rounded-lg bg-gray-100 dark:bg-gray-700 rounded-bl-sm">
                      {currentStreamingMessage ? (
                        <MarkdownMessage
                          content={currentStreamingMessage}
                          className="text-sm text-gray-900 dark:text-gray-100"
                        />
                      ) : (
                        <div className="text-sm text-gray-900 dark:text-gray-100">
                          {/* Empty state for when streaming just started */}
                        </div>
                      )}
                      <div className="flex items-center gap-1 text-xs opacity-70 mt-1">
                        <div className="flex gap-1">
                          <div
                            className="w-1 h-1 bg-current rounded-full animate-bounce"
                            style={{ animationDelay: "0ms" }}
                          ></div>
                          <div
                            className="w-1 h-1 bg-current rounded-full animate-bounce"
                            style={{ animationDelay: "150ms" }}
                          ></div>
                          <div
                            className="w-1 h-1 bg-current rounded-full animate-bounce"
                            style={{ animationDelay: "300ms" }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Auto-scroll anchor */}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Input Area */}
          <div className="p-3 border-t border-gray-200 dark:border-[#2d2d2d] bg-gray-50/50 dark:bg-[#252526]/50">
            {/* Interview Filter */}
            {readyInterviews.length > 0 && (
              <div className="mb-3">
                <button
                  onClick={() => setShowInterviewFilter(!showInterviewFilter)}
                  className={`flex items-center gap-2 text-xs transition-colors ${
                    selectedInterviewIds.length > 0
                      ? "text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
                  }`}
                >
                  <ChevronDown
                    className={`h-3 w-3 transition-transform ${
                      showInterviewFilter ? "" : "-rotate-90"
                    }`}
                  />
                  <span>
                    {selectedInterviewIds.length > 0
                      ? `${selectedInterviewIds.length} of ${readyInterviews.length} Interviews`
                      : `All ${readyInterviews.length} Interviews selected (default)`}
                  </span>
                </button>
                {!showInterviewFilter && selectedInterviewIds.length === 0 && (
                  <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 ml-5">
                    Click to filter specific interviews
                  </p>
                )}

                {showInterviewFilter && (
                  <div className="mt-2 bg-white dark:bg-[#2d2d2d] border border-gray-200 dark:border-[#404040] rounded-lg max-h-80 overflow-hidden flex flex-col">
                    {/* Filter Controls */}
                    <div className="p-2 border-b border-gray-200 dark:border-[#404040] space-y-2">
                      {/* Search */}
                      <input
                        type="text"
                        placeholder="Search interviews..."
                        value={filterSearch}
                        onChange={(e) => setFilterSearch(e.target.value)}
                        className="w-full px-2 py-1 text-xs bg-gray-50 dark:bg-[#1e1e1e] border border-gray-200 dark:border-[#404040] rounded focus:outline-none focus:ring-1 focus:ring-green-500 dark:focus:ring-green-400 text-gray-700 dark:text-gray-300 placeholder-gray-400"
                      />

                      {/* Filter Row */}
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Group By Toggle */}
                        <div className="flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400">
                          <span>Group:</span>
                          <button
                            onClick={() => setGroupBy("none")}
                            className={`px-1.5 py-0.5 rounded transition-colors ${
                              groupBy === "none"
                                ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                                : "hover:bg-gray-100 dark:hover:bg-[#333333]"
                            }`}
                          >
                            None
                          </button>
                          <button
                            onClick={() => setGroupBy("angle")}
                            className={`px-1.5 py-0.5 rounded transition-colors ${
                              groupBy === "angle"
                                ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                                : "hover:bg-gray-100 dark:hover:bg-[#333333]"
                            }`}
                          >
                            Angle
                          </button>
                        </div>

                        {/* Angle Filter Pills */}
                        {uniqueAngles.length > 0 && (
                          <div className="flex items-center gap-1 flex-wrap">
                            {uniqueAngles.map((angle) => {
                              const isSelected = angleFilters.includes(angle);
                              return (
                                <button
                                  key={angle}
                                  onClick={() => {
                                    setAngleFilters((prev) =>
                                      isSelected
                                        ? prev.filter((a) => a !== angle)
                                        : [...prev, angle]
                                    );
                                  }}
                                  className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${
                                    isSelected
                                      ? "bg-green-100 dark:bg-green-900/40 border-green-300 dark:border-green-700 text-green-700 dark:text-green-300"
                                      : "bg-gray-50 dark:bg-[#2a2a2a] border-gray-200 dark:border-[#404040] text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[#333333]"
                                  }`}
                                >
                                  {angle}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Grouped Interview List */}
                    <div className="overflow-y-auto p-2 flex-1">
                      {Object.entries(groupedInterviews).map(
                        ([groupName, groupInterviewList]) => {
                          const allGroupSelected = groupInterviewList.every(
                            (i) => selectedInterviewIds.includes(i.id)
                          );
                          const someGroupSelected =
                            !allGroupSelected &&
                            groupInterviewList.some((i) =>
                              selectedInterviewIds.includes(i.id)
                            );

                          return (
                            <div key={groupName} className="mb-2 last:mb-0">
                              {/* Group Header */}
                              {groupBy !== "none" && (
                                <div className="flex items-center justify-between mb-1 px-1">
                                  <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                    {groupName} ({groupInterviewList.length})
                                  </span>
                                  <button
                                    onClick={() =>
                                      toggleGroupSelection(groupInterviewList)
                                    }
                                    className={`text-[10px] px-1.5 py-0.5 rounded transition-colors ${
                                      allGroupSelected
                                        ? "text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20"
                                        : someGroupSelected
                                        ? "text-green-600/70 dark:text-green-400/70 hover:bg-gray-100 dark:hover:bg-[#333333]"
                                        : "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[#333333]"
                                    }`}
                                  >
                                    {allGroupSelected
                                      ? "Deselect All"
                                      : "Select All"}
                                  </button>
                                </div>
                              )}

                              {/* Interview Items */}
                              <div className="space-y-0.5">
                                {groupInterviewList.map((interview) => (
                                  <label
                                    key={interview.id}
                                    className="flex items-start gap-2 p-1.5 hover:bg-gray-50 dark:hover:bg-[#333333] rounded cursor-pointer transition-colors"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedInterviewIds.includes(
                                        interview.id
                                      )}
                                      onChange={() =>
                                        toggleInterviewSelection(interview.id)
                                      }
                                      className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 dark:border-gray-600 text-green-600 focus:ring-green-500 dark:focus:ring-green-400"
                                    />
                                    <div className="flex-1 min-w-0">
                                      <div className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                                        {interview.expertFirstName}{" "}
                                        {interview.expertLastName}
                                        {groupBy !== "company" &&
                                          interview.company && (
                                            <span className="font-normal text-gray-500 dark:text-gray-500">
                                              {" "}
                                              @ {interview.company}
                                            </span>
                                          )}
                                      </div>
                                      <div className="flex items-center gap-2 text-[10px] text-gray-500 dark:text-gray-400">
                                        {groupBy !== "angle" &&
                                          interview.angle && (
                                            <span className="px-1 py-0.5 bg-gray-100 dark:bg-[#505050] rounded text-gray-600 dark:text-gray-200">
                                              {interview.angle}
                                            </span>
                                          )}
                                        {interview.interviewDescription && (
                                          <span className="truncate">
                                            {interview.interviewDescription}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </label>
                                ))}
                              </div>
                            </div>
                          );
                        }
                      )}
                      {filteredInterviews.length === 0 && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 text-center py-4">
                          No interviews match your filters
                        </div>
                      )}
                    </div>

                    {/* Footer */}
                    {(selectedInterviewIds.length > 0 ||
                      angleFilters.length > 0 ||
                      filterSearch) && (
                      <div className="sticky bottom-0 p-2 pt-2 border-t border-gray-200 dark:border-[#404040] bg-white dark:bg-[#2d2d2d] flex items-center justify-between">
                        <button
                          onClick={() => {
                            setSelectedInterviewIds([]);
                          }}
                          className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
                        >
                          Clear Selection
                        </button>
                        {(angleFilters.length > 0 || filterSearch) && (
                          <button
                            onClick={() => {
                              setAngleFilters([]);
                              setFilterSearch("");
                            }}
                            className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
                          >
                            Clear Filters
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Images preview */}
            {images.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {images.map((img, idx) => (
                  <div
                    key={idx}
                    className="relative border border-gray-200 dark:border-gray-600 rounded"
                  >
                    <img
                      alt={`paste-${idx}`}
                      src={`data:${img.mime};base64,${img.b64}`}
                      className="h-12 w-12 object-cover rounded"
                    />
                    <button
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs"
                      onClick={() => removeImage(idx)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              <AutoExpandingTextarea
                value={msg}
                onChange={(e) => setMsg(e.target.value)}
                onPaste={onPaste}
                onKeyDown={handleKeyDown}
                onWheel={(e) => {
                  // Allow scrolling within textarea but prevent propagation to dashboard
                  e.stopPropagation();
                }}
                onTouchMove={(e) => e.stopPropagation()}
                onPointerMove={(e) => e.stopPropagation()}
                onScroll={(e) => e.stopPropagation()}
                placeholder="Ask about your transcripts... (Enter to send, Shift+Enter for newline)"
                className="flex-1 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm"
                minRows={2}
                maxRows={6}
              />
              {isStreaming ? (
                <button
                  onClick={cancelChat}
                  className="self-end h-10 w-10 rounded-full bg-gray-100 hover:bg-red-100 dark:bg-gray-700 dark:hover:bg-red-900/30 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 shadow-sm hover:shadow transition-all duration-200 flex items-center justify-center"
                  title="Cancel"
                >
                  <X className="h-4 w-4" />
                </button>
              ) : (
                <button
                  onClick={send}
                  disabled={!msg.trim()}
                  className="self-end h-10 w-10 rounded-full bg-gradient-to-br from-green-500 to-green-600 hover:from-green-400 hover:to-green-500 disabled:from-gray-300 disabled:to-gray-400 dark:disabled:from-gray-600 dark:disabled:to-gray-700 text-white shadow-md hover:shadow-lg disabled:shadow-none transition-all duration-200 flex items-center justify-center disabled:cursor-not-allowed"
                >
                  <Send className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Reset Warning Dialog */}
        {showResetWarning && (
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-[#1e1e1e] border border-gray-200 dark:border-[#2d2d2d] p-6 rounded-xl shadow-2xl max-w-sm w-full animate-in fade-in zoom-in-95 duration-200">
              <h3 className="font-medium mb-3 text-gray-900 dark:text-gray-100">
                Consider Resetting?
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                {messageCount >= MAX_MESSAGES
                  ? "This conversation is getting long. Consider resetting to help maintain AI performance and start with fresh context."
                  : "This will start a new conversation with fresh context."}
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowResetWarning(false)}
                  className="flex-1 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  Continue
                </Button>
                <Button
                  onClick={resetConversation}
                  className="flex-1 bg-blue-500 hover:bg-blue-600 text-white border-0 shadow-sm transition-colors"
                >
                  Reset
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Image Popup - Simple tooltip-style popup */}
      {selectedImage && popupPosition && (
        <div
          className="fixed pointer-events-none"
          style={{
            left: popupPosition.x,
            top: popupPosition.y,
            transform: "translate(-50%, -100%)",
            zIndex: 9999,
          }}
        >
          <div className="bg-background border shadow-xl rounded-lg p-2 max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg pointer-events-auto animate-in fade-in zoom-in-95 duration-200">
            {/* Close button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImage(null);
                setPopupPosition(null);
              }}
              className="absolute right-2 top-2 z-10 rounded-full p-1 bg-background/90 text-foreground hover:bg-accent transition-colors focus:outline-none focus:ring-2 focus:ring-ring shadow-sm"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </button>

            {/* Image */}
            <img
              src={`data:${selectedImage.mime};base64,${selectedImage.b64}`}
              alt="Full size attachment"
              className="w-full max-h-80 object-contain rounded"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </>
  );
};
export default ResizableChatSidebar;
